/**
 * Normalize any backend validation error value into a clean array of strings.
 */
const normalizeToArray = (val) => {
    if (Array.isArray(val)) {
        return val.filter(Boolean);
    }
    if (typeof val === 'string' && val) {
        return [val];
    }
    return [];
};

/**
 * Defines how validation errors should be extracted and shaped for each supported rule attribute.
 *
 * Supported modes:
 * - flat: Used when the attribute has a single logical error location.
 * - grouped: Used when the attribute has multiple inputs. Errors will be grouped by field name.
 */
const ATTRIBUTE_ERROR_SCHEMA = {
    required_fields: { mode: 'flat', fields: ['fields'] },
    gbc_score: { mode: 'grouped', fields: ['min_per_application', 'max_per_application', 'min_solo_applicant', 'max_solo_applicant'], },
    age_limits: { mode: 'grouped', fields: ['applicant_min_age', 'applicant_max_age', 'co_applicant_min_age', 'co_applicant_max_age'], },
    age_gap: { mode: 'grouped', fields: ['joint_age_gap_limit'] },
    amount_to_finance: { mode: 'grouped', fields: ['min_amount', 'max_amount'] },
    tdsr_ratio_value: { mode: 'grouped', fields: ['max_ratio'] },
    crp30_score: { mode: 'grouped', fields: ['min_score'] },
    ltd_value: { mode: 'grouped', fields: ['max_value'] },
    provincial_acceptance: { mode: 'flat', fields: ['allowed_provinces'] },
    customer_types: { mode: 'flat', fields: ['allowed_types'] },
    cohabitation_check: { mode: 'grouped', fields: ['must_be_true'] },
    consumer_statements_check: { mode: 'grouped', fields: ['must_be_true'] },
    bankruptcy_indicator_check: { mode: 'grouped', fields: ['must_be_true'] },
    provincial_match: { mode: 'grouped', fields: ['must_be_true'] },
    shopping_borrowers: { mode: 'grouped', fields: ['must_be_true'] },
    material_change: { mode: 'grouped', fields: ['must_be_true'] },
};

/**
 * Handles rule attribute validation errors for Rule Engine UI.
 */
export const useRuleAttributeErrors = ({ errorStore, localForm }) => {
    const getErrors = (key) => {
        if (!errorStore) {
            return [];
        }
        return normalizeToArray(errorStore.all?.[key]);
    };

    const findRuleIndexForPca = (pca) => {
        const attrId = pca?.product_attribute_id;
        if (!attrId) {
            return -1;
        }

        return (localForm.value.rules ?? []).findIndex(
            (rule) => rule.product_attribute_id === attrId
        );
    };

    const buildErrorKey = (ruleIndex, field) => `rules.${ruleIndex}.rule_json.${field}`;

    const errorsForPca = (pca) => {
        const attributeKey = pca?.attribute?.key;
        const schema = ATTRIBUTE_ERROR_SCHEMA[attributeKey];
        if (!schema) {
            return [];
        }

        const ruleIndex = findRuleIndexForPca(pca);
        if (ruleIndex === -1) {
            return [];
        }

        if (schema.mode === 'flat') {
            // returns Array<string>
            return getErrors(buildErrorKey(ruleIndex, schema.fields[0]));
        }

        if (schema.mode === 'grouped') {
            // returns { fieldName: Array<string> }
            const out = {};
            schema.fields.forEach((field) => {
                out[field] = getErrors(buildErrorKey(ruleIndex, field));
            });
            return out;
        }

        return [];
    };

    const clearErrorsForPca = (pca) => {
        const attributeKey = pca?.attribute?.key;
        const schema = ATTRIBUTE_ERROR_SCHEMA[attributeKey];
        if (!schema) {
            return;
        }

        const ruleIndex = findRuleIndexForPca(pca);
        if (ruleIndex === -1) {
            return;
        }

        schema.fields.forEach((field) => {
            errorStore?.remove(buildErrorKey(ruleIndex, field));
        });
    };

    const onAttributeTouched = (pca) => {
        clearErrorsForPca(pca);
    };

    return {
        errorsForPca,
        onAttributeTouched,
        clearErrorsForPca,
        findRuleIndexForPca,
        getErrors,
    };
};
